// missionTaskModalUtils.js

import { resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { normalizeChecklistTemplateItems } from 'utilities/ChecklistTemplateItems';
import {
  buildDurationAuthoringDraft,
  convertAuthoringDurationToMinutes,
} from 'missions/scheduling/missionScheduleUtils';
import {
  normalizeWorkflowCanvasPosition,
  normalizeWorkflowOutcomes,
} from 'workflows/workflowGraphUtils';

export const MISSION_TASK_HANDLED_BY = Object.freeze({
  BOT: 'bot',
  PEOPLE: 'people',
});

export const MISSION_BOT_JOB_OPTIONS = Object.freeze([
  {
    description: 'Send an email to a base member selected when the Mission is launched.',
    label: 'Send Message',
    value: 'sendMessage',
  },
  {
    description: 'Change a field on the current record when this task becomes ready.',
    label: 'Update Record',
    value: 'updateRecord',
  },
  {
    description: 'Create a related record with a title and optional starting values.',
    label: 'Create Record',
    value: 'createRecord',
  },
  {
    description: 'Describe flexible work in plain language for an Agent to run on this record.',
    label: 'Use an Agent',
    value: 'runWorkspaceCommand',
  },
]);

export const MISSION_BOT_SAFE_WRITABLE_FIELD_TYPES = Object.freeze([
  'shortText',
  'longText',
  'number',
  'boolean',
  'singleSelect',
  'date',
  'dateTime',
]);

export const MISSION_UPDATE_RECORD_MODE_ALLOWLIST = Object.freeze({
  shortText: Object.freeze(['setFixedValue', 'clearField']),
  longText: Object.freeze(['setFixedValue', 'clearField']),
  number: Object.freeze(['setFixedValue', 'clearField']),
  boolean: Object.freeze(['setFixedValue', 'clearField']),
  singleSelect: Object.freeze(['setFixedValue', 'clearField']),
  date: Object.freeze(['setFixedValue', 'useCurrentDateTime', 'clearField']),
  dateTime: Object.freeze(['setFixedValue', 'useCurrentDateTime', 'clearField']),
});

export const MISSION_CREATE_RECORD_INITIAL_VALUE_FIELD_TYPES = MISSION_BOT_SAFE_WRITABLE_FIELD_TYPES;

const FIELD_TYPE_ALIASES = Object.freeze({
  boolean: 'boolean',
  date: 'date',
  datetime: 'dateTime',
  email: 'email',
  enum: 'singleSelect',
  long_text: 'longText',
  longtext: 'longText',
  number: 'number',
  people: 'people',
  profile: 'profile',
  short_text: 'shortText',
  shorttext: 'shortText',
  single_select: 'singleSelect',
  singleselect: 'singleSelect',
  status: 'singleSelect',
  string: 'shortText',
  text: 'longText',
});

const MESSAGE_DETAIL_EXCLUDED_FIELD_TYPES = new Set([
  'checklist',
  'color',
  'file',
  'gallery',
  'location',
  'media',
  'nodelist',
  'people',
  'photo',
  'profile',
  'richtext',
  'tags',
  'video',
]);

const UPDATE_MODE_LABELS = Object.freeze({
  clearField: 'Clear the field',
  setFixedValue: 'Set a fixed value',
  useCurrentDateTime: 'Use current date/time',
});

const BOT_JOB_LABEL_BY_VALUE = new Map(
  MISSION_BOT_JOB_OPTIONS.map((option) => [option.value, option.label])
);

const normalizeString = (value) => (typeof value === 'string' ? value.trim() : '');

const toKeyString = (value) => String(value || '').trim();

const normalizeObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

export const MISSION_MESSAGE_DETAIL_TITLE_KEY = '__recordTitle';

export const getMissionTaskPersonKey = (person) => toKeyString(person?.userKey || person?.key);

export const getMissionTaskPersonLabel = (person) => {
  const candidates = [
    person?.name,
    person?.data_profile?.name,
    person?.title,
    person?.email,
    person?.userKey,
    person?.key,
  ];

  const label = candidates.find((candidate) => normalizeString(candidate));
  return normalizeString(label);
};

export function normalizeMissionPeople(value) {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  const map = new Map();

  list.forEach((entry) => {
    if (!entry) return;

    const key = getMissionTaskPersonKey(entry);
    if (!key) return;

    map.set(key, {
      coverUrl: entry?.coverUrl || '',
      data_profile: {
        email: entry?.email || entry?.data_profile?.email || '',
        name: getMissionTaskPersonLabel(entry),
      },
      email: entry?.email || entry?.data_profile?.email || '',
      key,
      name: getMissionTaskPersonLabel(entry),
      title: normalizeString(entry?.title) || getMissionTaskPersonLabel(entry),
      userKey: key,
    });
  });

  return Array.from(map.values());
}

export function normalizeMissionTaskKeyList(value, options = {}) {
  const excludedKey = toKeyString(options?.excludedKey);
  const list = Array.isArray(value) ? value : value ? [value] : [];
  const seen = new Set();

  return list
    .map((entry) => {
      if (typeof entry === 'string' || typeof entry === 'number') {
        return toKeyString(entry);
      }

      return toKeyString(entry?.taskKey || entry?.key || entry?.value);
    })
    .filter((entry) => {
      if (!entry || entry === excludedKey || seen.has(entry)) return false;
      seen.add(entry);
      return true;
    });
}

export const getMissionFieldKey = (field) => toKeyString(field?.key || field?.name || field?.value);

export const getMissionFieldLabel = (field) => {
  const key = getMissionFieldKey(field);
  return normalizeString(field?.label || field?.title || key) || key;
};

export const getMissionFieldDescription = (field) => (
  normalizeString(field?.fieldDescription || field?.description)
);

export function normalizeMissionBotFieldType(fieldType) {
  const normalized = normalizeString(fieldType).toLowerCase();
  return FIELD_TYPE_ALIASES[normalized] || '';
}

export function getMissionFieldEnumOptions(field) {
  return resolveEnumOptionsForField(field, 'en').map((option) => ({
    label: option?.translations?.en?.label || option?.value || '',
    value: option?.value || '',
  }));
}

export function getMissionFieldMeta(field) {
  const key = getMissionFieldKey(field);
  const normalizedType = normalizeMissionBotFieldType(field?.type || field?.fieldType);

  return {
    description: getMissionFieldDescription(field),
    enumOptions: getMissionFieldEnumOptions(field),
    key,
    label: getMissionFieldLabel(field),
    rawField: field,
    rawType: normalizeString(field?.type || field?.fieldType).toLowerCase(),
    type: normalizedType,
  };
}

export function getMissionSendMessageEffectiveChannel(sendMessage = {}) {
  return 'email';
}

export function getMissionBotWritableFields(fields) {
  return (Array.isArray(fields) ? fields : [])
    .map((field) => getMissionFieldMeta(field))
    .filter((field) => field.key && MISSION_BOT_SAFE_WRITABLE_FIELD_TYPES.includes(field.type));
}

export function getMissionMessageDetailFields(fields) {
  const detailFields = (Array.isArray(fields) ? fields : [])
    .map((field) => ({
      description: getMissionFieldDescription(field),
      key: getMissionFieldKey(field),
      label: getMissionFieldLabel(field),
      rawType: normalizeString(field?.type || field?.fieldType).toLowerCase(),
    }))
    .filter((field) => field.key && !MESSAGE_DETAIL_EXCLUDED_FIELD_TYPES.has(field.rawType));

  return [
    {
      description: 'The record title.',
      key: MISSION_MESSAGE_DETAIL_TITLE_KEY,
      label: 'Title',
      rawType: 'title',
    },
    ...detailFields,
  ];
}

export function getMissionUpdateModesForField(field) {
  if (!field?.type) return [];
  const modeKeys = MISSION_UPDATE_RECORD_MODE_ALLOWLIST[field.type] || [];

  return modeKeys.map((mode) => ({
    label: UPDATE_MODE_LABELS[mode] || mode,
    value: mode,
  }));
}

export function findMissionRecordType(recordTypeOptions, recordTypeKey) {
  const targetKey = toKeyString(recordTypeKey);
  if (!targetKey) return null;

  return (Array.isArray(recordTypeOptions) ? recordTypeOptions : []).find((option) => {
    const optionKey = toKeyString(option?.key || option?.value || option?.typeKey);
    return optionKey === targetKey;
  }) || null;
}

export function getMissionRecordTypeLabel(recordType) {
  return normalizeString(
    recordType?.label
      || recordType?.title
      || recordType?.name
      || recordType?.singularLabel
      || recordType?.pluralLabel
      || recordType?.key
  );
}

export function getMissionRecordTypeFields(recordType) {
  if (!recordType || typeof recordType !== 'object') return [];

  const candidates = [
    recordType?.fields,
    recordType?.schema?.fields,
    recordType?.resolvedProperties,
    recordType?.properties,
  ];

  const fields = candidates.find((candidate) => Array.isArray(candidate));
  return Array.isArray(fields) ? fields : [];
}

export function getMissionCreateRecordFields(recordTypeOptions, recordTypeKey) {
  const recordType = findMissionRecordType(recordTypeOptions, recordTypeKey);
  if (!recordType) return [];
  return getMissionBotWritableFields(getMissionRecordTypeFields(recordType));
}

export function buildMissionTaskDraft(task = {}) {
  const normalizedTask = normalizeObject(task);
  const missionBot = normalizeObject(normalizedTask?.missionBot || normalizedTask?.bot);
  const missionBotConfig = normalizeObject(missionBot?.config);
  const sendMessage = normalizeObject(normalizedTask?.sendMessage || missionBotConfig?.sendMessage || missionBotConfig);
  const updateRecord = normalizeObject(normalizedTask?.updateRecord || missionBotConfig?.updateRecord || missionBotConfig);
  const createRecord = normalizeObject(normalizedTask?.createRecord || missionBotConfig?.createRecord || missionBotConfig);
  const runWorkspaceCommand = normalizeObject(
    normalizedTask?.runWorkspaceCommand || missionBotConfig?.runWorkspaceCommand || missionBotConfig
  );
  const handledBy = normalizeString(normalizedTask?.handledBy).toLowerCase() === MISSION_TASK_HANDLED_BY.BOT
    ? MISSION_TASK_HANDLED_BY.BOT
    : MISSION_TASK_HANDLED_BY.PEOPLE;
  const durationDraft = buildDurationAuthoringDraft({
    ...normalizedTask,
    handledBy,
  });

  return {
    assignees: normalizeMissionPeople(
      normalizedTask?.assignees
      || normalizedTask?.assignedPeople
      || normalizedTask?.people
      || normalizedTask?.assignedTo
    ),
    botJob: normalizeString(missionBot?.job || normalizedTask?.botJob),
    createRecord: {
      initialValues: normalizeObject(createRecord?.initialValues),
      recordTypeKey: toKeyString(createRecord?.recordTypeKey || createRecord?.typeKey),
      title: String(createRecord?.title || ''),
    },
    description: String(normalizedTask?.description || normalizedTask?.instructions || normalizedTask?.guidance || ''),
    canvas: normalizeWorkflowCanvasPosition(normalizedTask?.canvas),
    estimatedDurationAmount: durationDraft.amount,
    estimatedDurationUnit: durationDraft.unit,
    handledBy,
    outcomes: normalizeWorkflowOutcomes(normalizedTask?.outcomes, { handledBy }),
    prerequisiteTaskKeys: normalizeMissionTaskKeyList(
      normalizedTask?.prerequisiteTaskKeys || normalizedTask?.prerequisites
    ),
    runWorkspaceCommand: {
      instruction: String(runWorkspaceCommand?.instruction || ''),
      previewSummary: String(runWorkspaceCommand?.previewSummary || ''),
    },
    sendMessage: {
      includeRecordFieldKeys: Array.isArray(sendMessage?.includeRecordFieldKeys)
        ? sendMessage.includeRecordFieldKeys.map((entry) => toKeyString(entry)).filter(Boolean)
        : [],
      message: String(sendMessage?.message || ''),
      subject: String(sendMessage?.subject || ''),
    },
    taskChecklistItems: normalizeChecklistTemplateItems(
      normalizedTask?.taskChecklistItems || normalizedTask?.checklistItems
    ),
    taskChecklistRequired: normalizedTask?.taskChecklistRequired === true,
    title: String(normalizedTask?.title || normalizedTask?.name || ''),
    updateRecord: {
      fieldKey: toKeyString(updateRecord?.fieldKey),
      mode: normalizeString(updateRecord?.mode),
      value: updateRecord?.value ?? '',
    },
  };
}

export function buildMissionTaskPayload(draft, existingTask = {}) {
  const normalizedExistingTask = normalizeObject(existingTask);
  const nextTask = {
    ...normalizedExistingTask,
    // People-step owners come from the host record at launch — do not persist
    // per-task default assignees on the template.
    assignees: [],
    description: String(draft.description || ''),
    handledBy: draft.handledBy,
    missionBot: null,
    outcomes: draft.handledBy === MISSION_TASK_HANDLED_BY.PEOPLE
      ? normalizeWorkflowOutcomes(draft.outcomes, { handledBy: draft.handledBy })
        .map((outcome) => ({
          ...outcome,
          label: String(outcome.label || '').trim() || outcome.key,
        }))
      : [],
    title: String(draft.title || '').trim(),
  };

  const canvas = normalizeWorkflowCanvasPosition(draft.canvas || normalizedExistingTask?.canvas);
  if (canvas) {
    nextTask.canvas = canvas;
  } else {
    delete nextTask.canvas;
  }

  // Graph edges own sequencing; do not persist prerequisites on new writes.
  delete nextTask.prerequisiteTaskKeys;
  delete nextTask.prerequisites;

  const normalizedDurationMinutes = convertAuthoringDurationToMinutes(
    draft.estimatedDurationAmount,
    draft.estimatedDurationUnit
  );

  if (normalizedDurationMinutes > 0 && draft.handledBy === MISSION_TASK_HANDLED_BY.PEOPLE) {
    nextTask.estimatedDurationMinutes = normalizedDurationMinutes;
  } else {
    delete nextTask.estimatedDurationMinutes;
  }

  delete nextTask.estimatedDurationDays;
  delete nextTask.dueAtMs;
  delete nextTask.dueTiming;
  delete nextTask.dueTimingDays;
  delete nextTask.guidance;
  delete nextTask.instructions;
  delete nextTask.required;
  delete nextTask.checklistItems;

  if (draft.handledBy === MISSION_TASK_HANDLED_BY.PEOPLE) {
    const checklistItems = normalizeChecklistTemplateItems(draft.taskChecklistItems);
    if (checklistItems.length > 0) {
      nextTask.taskChecklistItems = checklistItems;
      nextTask.taskChecklistRequired = draft.taskChecklistRequired === true;
    } else {
      delete nextTask.taskChecklistItems;
      delete nextTask.taskChecklistRequired;
    }
  } else {
    delete nextTask.taskChecklistItems;
    delete nextTask.taskChecklistRequired;
  }

  if (draft.handledBy === MISSION_TASK_HANDLED_BY.BOT) {
    nextTask.missionBot = {
      config: buildMissionBotConfig(draft),
      job: draft.botJob,
    };
  }

  return nextTask;
}

export function normalizeMissionTask(task = {}) {
  const normalizedTask = buildMissionTaskPayload(buildMissionTaskDraft(task), task);
  const normalizedKey = toKeyString(task?.key || task?.taskKey);

  return {
    ...normalizedTask,
    key: normalizedKey,
  };
}

export function getMissionBotJobLabel(botJob) {
  return BOT_JOB_LABEL_BY_VALUE.get(normalizeString(botJob)) || 'Bot Task';
}

export function buildMissionTaskSummary(task = {}) {
  const normalizedTask = normalizeMissionTask(task);

  if (normalizedTask.handledBy === MISSION_TASK_HANDLED_BY.BOT) {
    return ['Bot', getMissionBotJobLabel(normalizedTask?.missionBot?.job)]
      .filter(Boolean)
      .join(' • ');
  }

  // Duration lives in the task header time spot (allotted / timer / actual).
  // Owners come from the host record at launch, not from template defaults.
  return 'People';
}

export function buildMissionBotConfig(draft) {
  if (draft.botJob === 'sendMessage') {
    return {
      sendMessage: {
        includeRecordFieldKeys: Array.isArray(draft.sendMessage.includeRecordFieldKeys)
          ? draft.sendMessage.includeRecordFieldKeys.map((entry) => toKeyString(entry)).filter(Boolean)
          : [],
        message: String(draft.sendMessage.message || ''),
        subject: String(draft.sendMessage.subject || ''),
      },
    };
  }

  if (draft.botJob === 'updateRecord') {
    return {
      updateRecord: {
        fieldKey: toKeyString(draft.updateRecord.fieldKey),
        mode: normalizeString(draft.updateRecord.mode),
        value: draft.updateRecord.value ?? '',
      },
    };
  }

  if (draft.botJob === 'createRecord') {
    return {
      createRecord: {
        initialValues: normalizeObject(draft.createRecord.initialValues),
        recordTypeKey: toKeyString(draft.createRecord.recordTypeKey),
        title: String(draft.createRecord.title || ''),
      },
    };
  }

  if (draft.botJob === 'runWorkspaceCommand') {
    return {
      runWorkspaceCommand: {
        instruction: String(draft.runWorkspaceCommand?.instruction || '').trim(),
        previewSummary: String(draft.runWorkspaceCommand?.previewSummary || '').trim(),
      },
    };
  }

  return {};
}

export function hasMissionValue(value, fieldType) {
  if (fieldType === 'boolean') {
    return value === true || value === false;
  }

  if (fieldType === 'number') {
    return value !== '' && value != null && Number.isFinite(Number(value));
  }

  return normalizeString(value).length > 0;
}
